import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChartLineUp, Database } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import ContextArticles from "../components/ContextArticles";
import CompareView from "../components/timing/CompareView";
import Finders from "../components/timing/Finders";
import PoolAnalyzer from "../components/timing/PoolAnalyzer";
import SeasonSimulator from "../components/timing/SeasonSimulator";
import FixtureMatrix from "../components/timing/FixtureMatrix";
import TransfersBoard from "../components/timing/TransfersBoard";
import HaulsBoard from "../components/timing/HaulsBoard";
import FdrPerfBoard from "../components/timing/FdrPerfBoard";
import { timingState, timingIngest } from "../lib/timing-api";
import { t } from "../lib/timing-i18n";
import { useSharedPool } from "../lib/timing-pool-store";

// Meta jednotlivých nástrojů — každý má vlastní obal dle fáze workflow.
const TOOL_META = {
    pool: { phase: "Manager Lab · Analyze", labelKey: "tabPool", desc: "Analýza tvého poolu kandidátů — 6+ sezón historie, fáze sezóny a typologie týmu." },
    compare: { phase: "Manager Lab · Select", labelKey: "tabCompare", desc: "Porovnej 2–4 hráče — bodové křivky a metriky napříč sezónami." },
    finders: { phase: "Manager Lab · Analyze", labelKey: "tabFinders", desc: "Leaderboardy: konzistence, výbušnost, value, fáze sezóny, síla soupeře." },
    matrix: { phase: "Manager Lab · Analyze", labelKey: "tabMatrix", desc: "Projekce bodů hráč × GW dle rozpisu 2026/27." },
    simulator: { phase: "Manager Lab · Play", labelKey: "tabSimulator", desc: "Simulace celé sezóny včetně plánovaných přestupů." },
    transfers: { phase: "Manager Lab · Analyze", labelKey: "tabTransfers", desc: "Potvrzené reálné přestupy okna 2026 s FPL dopadem." },
    hauls: { phase: "Manager Lab · Analyze", labelKey: "tabHauls", desc: "Kdo nejčastěji dává 10+ bodů — historie 2016–2026." },
    fdrperf: { phase: "Manager Lab · Analyze", labelKey: "tabFdrPerf", desc: "PPG podle síly soupeře (FDR 1–5)." },
};

const TOOL_ARTICLE_CATEGORY = {
    pool: "strategy", explorer: "players", compare: "strategy", finders: "data_lab",
    matrix: "data_lab", simulator: "strategy", transfers: "players",
    hauls: "data_lab", fdrperf: "data_lab",
};

// Standalone tool routes — each old tab now lives on its own page.
export const TAB_ROUTES = {
    pool: "/tools/season-dna",
    explorer: "/players",
    compare: "/tools/compare",
    finders: "/tools/finders",
    matrix: "/tools/fixture-matrix",
    simulator: "/tools/season-simulator",
    transfers: "/tools/transfers",
    hauls: "/tools/hauls",
    fdrperf: "/tools/fdr-analyzer",
};

function relativeAge(iso, lang) {
    if (!iso) return null;
    try {
        const then = new Date(iso).getTime();
        const now = Date.now();
        const s = Math.max(1, Math.round((now - then) / 1000));
        if (s < 60) return lang === "cs" ? `před ${s} s` : `${s}s ago`;
        const m = Math.round(s / 60);
        if (m < 60) return lang === "cs" ? `před ${m} min` : `${m}m ago`;
        const h = Math.round(m / 60);
        if (h < 24) return lang === "cs" ? `před ${h} h` : `${h}h ago`;
        const d = Math.round(h / 24);
        return lang === "cs" ? `před ${d} d` : `${d}d ago`;
    } catch { return null; }
}

export default function TimingAnalyzer({ fixedTab = "pool" }) {
    const navigate = useNavigate();
    const [lang, setLang] = useState(() => {
        try { return localStorage.getItem("timing.lang") || "cs"; }
        catch { return "cs"; }
    });
    const tab = fixedTab;
    // Přepnutí nástroje = navigace na jeho vlastní route.
    const setTab = (key) => navigate(TAB_ROUTES[key] || TAB_ROUTES.pool);
    const meta = TOOL_META[tab] || TOOL_META.pool;
    const [state, setState] = useState(null);
    const [ingesting, setIngesting] = useState(false);
    const sharedPool = useSharedPool();

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const s = await timingState();
                if (cancelled) return;
                setState(s);
                // Auto-poll if any season is partial (< 15k rows) — data still ingesting.
                const partial = (s.seasons_available || []).some(
                    (k) => (s.seasons?.[k] || 0) < 15000
                );
                if (partial) setTimeout(load, 6000);
            } catch { /* ignore */ }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    const switchLang = (l) => {
        setLang(l);
        try { localStorage.setItem("timing.lang", l); } catch { /* ignore */ }
    };

    const doIngest = async () => {
        setIngesting(true);
        try {
            await timingIngest();
            toast.success("Ingest spuštěn — data se natáhnou během ~30-60s");
            // Poll /state every 4s for up to 3 minutes to reflect live progress.
            let ticks = 0;
            const maxTicks = 45;
            const poll = setInterval(async () => {
                ticks += 1;
                try {
                    const s = await timingState();
                    setState(s);
                    const complete = s.seasons_available.every((k) => (s.seasons?.[k] || 0) > 10000);
                    if (complete || ticks >= maxTicks) {
                        clearInterval(poll);
                        setIngesting(false);
                        if (complete) toast.success("Data hotová");
                    }
                } catch { /* ignore transient errors */ }
            }, 4000);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Ingest failed");
            setIngesting(false);
        }
    };

    return (
        <AppShell>
            <section className="space-y-4">
                {/* HERO */}
                <header className="border-2 border-fa-sky/40 bg-gradient-to-br from-fa-sky/10 via-transparent to-fa-mint/5 p-6"
                        data-testid="timing-hero">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-sky flex items-center gap-2">
                                <ChartLineUp size={11} weight="fill" />
                                {meta.phase}
                            </div>
                            <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight mt-1">
                                {t(meta.labelKey, lang)}
                            </h1>
                            <p className="font-mono text-[11px] text-white/65 mt-2 max-w-3xl leading-relaxed">
                                {meta.desc}
                            </p>
                        </div>
                        {/* Lang switch */}
                        <div className="flex items-center gap-1.5" data-testid="timing-lang">
                            {[["en", "EN"], ["cs", "CS"]].map(([k, l]) => (
                                <button
                                    key={k}
                                    type="button"
                                    onClick={() => switchLang(k)}
                                    data-testid={`timing-lang-${k}`}
                                    className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${lang === k ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}
                                >
                                    {l}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Data state chip (jen hlavní DNA nástroj) */}
                    {state && tab === "pool" && (
                        <div className="mt-3 flex items-center gap-3 flex-wrap font-mono text-[10px] uppercase tracking-widest text-white/50"
                             data-testid="timing-state">
                            <span className="text-fa-sky flex items-center gap-1.5">
                                <Database size={10} weight="fill" /> {t("state", lang)}
                            </span>
                            {(state.seasons_available || []).map((s) => {
                                const rows = state.seasons?.[s] || 0;
                                const partial = rows < 15000;
                                return (
                                    <span key={s} className="flex items-center gap-1">
                                        <span className={partial ? "text-orange-300" : "text-white/40"}>{s}</span>
                                        <span className={partial ? "text-orange-300 tabular-nums" : "text-white tabular-nums"}>
                                            {rows.toLocaleString("cs")}
                                        </span>
                                        {partial && <span className="text-orange-300 text-[8px]">⚠</span>}
                                    </span>
                                );
                            })}
                            <span>· {t("totalPlayers", lang)}: <strong className="text-white tabular-nums">{state.total_players}</strong></span>
                            {state.last_ingest_latest && (
                                <span
                                    data-testid="timing-last-ingest"
                                    title={state.last_ingest_latest}
                                    className="flex items-center gap-1 text-white/50"
                                >
                                    <span className="text-fa-mint">●</span>
                                    <span>Aktualizováno {relativeAge(state.last_ingest_latest, lang)}</span>
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={doIngest}
                                disabled={ingesting}
                                data-testid="timing-ingest-btn"
                                className="ml-auto font-mono text-[10px] uppercase tracking-widest text-fa-sky border border-fa-sky/40 px-2 py-1 hover:bg-fa-sky hover:text-black disabled:opacity-50"
                            >
                                {ingesting ? t("loading", lang) : t("ingest", lang)}
                            </button>
                        </div>
                    )}
                </header>

                {/* Tool content */}
                {tab === "pool" && <PoolAnalyzer lang={lang} sharedPool={sharedPool} onSwitchTab={setTab} />}
                {tab === "compare" && <CompareView lang={lang} />}
                {tab === "finders" && <Finders lang={lang} sharedPool={sharedPool} />}
                {tab === "matrix" && <FixtureMatrix lang={lang} sharedPool={sharedPool} />}
                {tab === "simulator" && <SeasonSimulator lang={lang} sharedPool={sharedPool} />}
                {tab === "transfers" && <TransfersBoard lang={lang} />}
                {tab === "hauls" && <HaulsBoard />}
                {tab === "fdrperf" && <FdrPerfBoard />}
                <ContextArticles category={TOOL_ARTICLE_CATEGORY[tab] || "data_lab"} />
            </section>
        </AppShell>
    );
}
